import React from 'react';
import {
  ArrowLeftRight, Bell, Crown, ImagePlus, LogOut, Menu, MonitorDown, Moon, Plus, Search, Sun,
} from 'lucide-react';
import Logo from '../../../components/Logo/Logo';
import { AvatarPicker } from '../../../components/AvatarPicker/AvatarPicker';
import { resolveUserAvatar, resolveUserDisplayName } from '../../../utils/userAvatar';
import { DASHBOARD_BASE } from '../../../config/featureFlags';
import { get, post } from '../../../modules/tasks/api/client';
import { timeAgo } from '../../../modules/tasks/utils/format';

const NOTIF_KIND = {
  reminder: 'Reminder',
  assign: 'Assigned',
  propose: 'Proposed',
  mention: 'Mention',
  comment: 'Comment',
  due: 'Due',
  completed: 'Done',
  stale: 'Needs a look',
  suggestions: 'Untracked',
  idle: 'Idle time',
};

function initialsOf(user) {
  const source = (resolveUserDisplayName(user) || user?.email || '?').trim();
  const words = source.split(/[\s._@-]+/).filter(Boolean);
  return (words.length > 1 ? words[0][0] + words[1][0] : source.slice(0, 2)).toLowerCase();
}

// A person, not a record: prefer a real name, fall back to the readable part of
// the address rather than repeating the whole address as a heading.
function displayNameOf(user) {
  const full = resolveUserDisplayName(user);
  if (full) return full;
  const local = (user?.email || '').split('@')[0];
  return local ? local.replace(/[._-]+/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()) : 'Account';
}

function roleLabelOf(user) {
  if (!user?.role) return null;
  return String(user.role).replace(/[._-]+/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

function NotificationBell({ tasksEnabled, idleCount = 0, idleHref, onNavigate }) {
  const [open, setOpen] = React.useState(false);
  const [leaving, setLeaving] = React.useState(false);
  const [rows, setRows] = React.useState([]);
  const [edges, setEdges] = React.useState({ top: false, bottom: false });
  const ref = React.useRef(null);
  const listRef = React.useRef(null);
  const leaveTimer = React.useRef(null);
  const inboxHref = tasksEnabled ? `${DASHBOARD_BASE}/tasks/inbox` : idleHref;
  const visible = open || leaving;

  const load = React.useCallback(async () => {
    if (!tasksEnabled) { setRows([]); return; }
    try {
      const data = await get('/notifications');
      setRows(Array.isArray(data) ? data : []);
    } catch (_e) {
      setRows([]);
    }
  }, [tasksEnabled]);

  React.useEffect(() => {
    load();
    if (!tasksEnabled) return undefined;
    const id = setInterval(load, 60 * 1000);
    const onPing = () => load();
    window.addEventListener('tg-notifications', onPing);
    return () => {
      clearInterval(id);
      window.removeEventListener('tg-notifications', onPing);
    };
  }, [load, tasksEnabled]);

  const syncEdges = React.useCallback(() => {
    const el = listRef.current;
    if (!el) { setEdges({ top: false, bottom: false }); return; }
    setEdges({
      top: el.scrollTop > 6,
      bottom: el.scrollHeight - el.scrollTop - el.clientHeight > 6,
    });
  }, []);

  const close = React.useCallback(() => {
    if (!open || leaving) return;
    setLeaving(true);
    clearTimeout(leaveTimer.current);
    leaveTimer.current = setTimeout(() => { setOpen(false); setLeaving(false); }, 160);
  }, [open, leaving]);

  React.useEffect(() => () => clearTimeout(leaveTimer.current), []);

  React.useEffect(() => {
    if (!visible) return undefined;
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) close(); };
    const onEsc = (e) => { if (e.key === 'Escape') close(); };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onEsc);
    const id = requestAnimationFrame(syncEdges);
    return () => {
      cancelAnimationFrame(id);
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onEsc);
    };
  }, [visible, close, syncEdges, rows, idleCount]);

  const unreadTasks = rows.filter((n) => !n.readAt).length;
  const count = unreadTasks + (idleCount > 0 ? idleCount : 0);

  const openItem = async (n) => {
    close();
    if (n.id && n.type !== 'idle' && !n.readAt) {
      setRows((prev) => prev.map((r) => (r.id === n.id ? { ...r, readAt: new Date().toISOString() } : r)));
      post('/notifications/read', { ids: [n.id] }).catch(() => {});
    }
    if (n.href) onNavigate(n.href);
    else if (n.taskId) onNavigate(`${DASHBOARD_BASE}/tasks/t/${n.taskId}`);
  };

  const markAll = () => {
    if (!unreadTasks) return;
    setRows((prev) => prev.map((r) => ({ ...r, readAt: r.readAt || new Date().toISOString() })));
    post('/notifications/read', { all: true }).catch(() => {});
  };

  const items = [];
  if (idleHref && idleCount > 0) {
    items.push({
      id: 'idle',
      type: 'idle',
      text: `${idleCount} idle time request${idleCount === 1 ? '' : 's'} waiting`,
      href: idleHref,
      readAt: null,
    });
  }
  rows.slice(0, 20).forEach((n) => items.push(n));

  return (
    <div ref={ref} className="relative flex-shrink-0">
      <button
        type="button"
        className={`tg-icon-btn relative tg-bell-btn${open && !leaving ? ' is-open' : ''}`}
        aria-label={count ? `Notifications, ${count} unread` : 'Notifications'}
        aria-haspopup="true"
        aria-expanded={open && !leaving}
        title="Notifications"
        onClick={() => {
          if (open && !leaving) close();
          else { setLeaving(false); setOpen(true); load(); }
        }}
      >
        <Bell className="h-4 w-4 tg-bell-icon" />
        {count > 0 && <span className="tg-bell-badge" key={count}>{count > 9 ? '9+' : count}</span>}
      </button>
      {visible && (
        <div className={`tg-user-menu is-below tg-notif-menu${leaving ? ' is-leave' : ' is-enter'}`} role="menu">
          <div className="tg-notif-hd">
            <span>Notifications</span>
            <button type="button" className="tg-notif-link" disabled={!unreadTasks} onClick={markAll}>Mark all read</button>
          </div>
          <div className={`tg-notif-list${edges.top ? ' has-top' : ''}${edges.bottom ? ' has-bot' : ''}`}>
            <div ref={listRef} className="tg-notif-scroll" onScroll={syncEdges}>
              {items.length === 0 ? (
                <div className="tg-notif-empty">Assignments, mentions and reminders land here.</div>
              ) : items.map((n, i) => (
                <button
                  key={n.id}
                  type="button"
                  role="menuitem"
                  style={{ '--i': i }}
                  className={`tg-notif-row${n.readAt ? '' : ' is-unread'}`}
                  onClick={() => openItem(n)}
                >
                  <span className="tg-notif-pip" aria-hidden="true" />
                  <span className="tg-notif-kind">{NOTIF_KIND[n.type] || 'Update'}</span>
                  <span className="tg-notif-text">{n.text || 'Notification'}</span>
                  {n.createdAt && <span className="tg-notif-time">{timeAgo(n.createdAt)}</span>}
                </button>
              ))}
            </div>
          </div>
          {inboxHref && (
            <button type="button" className="tg-notif-all" onClick={() => { close(); onNavigate(inboxHref); }}>
              View all
            </button>
          )}
        </div>
      )}
    </div>
  );
}

// Account menu. Avatar only in the chrome; identity, theme and sign-out live
// inside the popover so the bar stays a bar.
function AccountMenu({
  user, isDark, onToggleTheme, onLogout, onNavigate, showSwitchBack, onSwitchBack,
}) {
  const [open, setOpen] = React.useState(false);
  const [pickerOpen, setPickerOpen] = React.useState(false);
  const [avatarBroken, setAvatarBroken] = React.useState(false);
  const ref = React.useRef(null);

  const avatarSrc = resolveUserAvatar(user);
  React.useEffect(() => { setAvatarBroken(false); }, [avatarSrc]);

  React.useEffect(() => {
    if (!open) return undefined;
    const onDocClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    const onEsc = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onEsc);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onEsc);
    };
  }, [open]);

  return (
    <div ref={ref} className="relative flex-shrink-0">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label="Account"
        className={`tg-user-trigger is-collapsed ${open ? 'is-open' : ''}`}
      >
        {avatarSrc && !avatarBroken ? (
          <img className="tg-avatar" src={avatarSrc} alt="" onError={() => setAvatarBroken(true)} />
        ) : (
          <span className="tg-avatar" aria-hidden="true">{initialsOf(user)}</span>
        )}
      </button>

      {open && (
        <div className="tg-user-menu is-below" role="menu">
          <div className="tg-user-card">
            {avatarSrc && !avatarBroken ? (
              <img className="tg-avatar is-lg" src={avatarSrc} alt="" onError={() => setAvatarBroken(true)} />
            ) : (
              <span className="tg-avatar is-lg" aria-hidden="true">{initialsOf(user)}</span>
            )}
            <div className="min-w-0">
              <div className="tg-user-name" title={displayNameOf(user)}>{displayNameOf(user)}</div>
              {user?.email && <div className="tg-user-email" title={user.email}>{user.email}</div>}
              {(showSwitchBack || roleLabelOf(user)) && (
                <span className="tg-user-role">{showSwitchBack ? 'Main Admin' : roleLabelOf(user)}</span>
              )}
            </div>
          </div>
          <div className="tg-nav-divider" />
          <button type="button" role="menuitem" className="tg-nav-item" onClick={() => { setOpen(false); setPickerOpen(true); }}>
            <ImagePlus className="tg-nav-icon mr-3 h-4 w-4" />
            Change avatar
          </button>
          {showSwitchBack && (
            <button type="button" role="menuitem" className="tg-nav-item" onClick={() => { setOpen(false); onSwitchBack(); }}>
              <ArrowLeftRight className="tg-nav-icon mr-3 h-4 w-4" />
              Back to Main Admin
            </button>
          )}
          <button type="button" role="menuitem" className="tg-nav-item" onClick={() => { setOpen(false); onNavigate('/download'); }}>
            <MonitorDown className="tg-nav-icon mr-3 h-4 w-4" />
            Download Desktop App
          </button>
          <button type="button" role="menuitem" className="tg-nav-item" onClick={() => { setOpen(false); onToggleTheme(); }}>
            {isDark ? <Sun className="tg-nav-icon mr-3 h-4 w-4" /> : <Moon className="tg-nav-icon mr-3 h-4 w-4" />}
            Switch to {isDark ? 'Light' : 'Dark'} mode
          </button>
          <button type="button" role="menuitem" className="tg-nav-item" onClick={() => { setOpen(false); onLogout(); }}>
            <LogOut className="tg-nav-icon mr-3 h-4 w-4" />
            Logout
          </button>
        </div>
      )}
      <AvatarPicker isOpen={pickerOpen} onClose={() => setPickerOpen(false)} />
    </div>
  );
}

// Create menu: the one brand-colored affordance in the chrome, sitting right
// after the search so "find something" and "make something" share a spot.
function CreateButton({ actions }) {
  const [open, setOpen] = React.useState(false);
  const ref = React.useRef(null);

  React.useEffect(() => {
    if (!open) return undefined;
    const onDocClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    const onEsc = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onEsc);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onEsc);
    };
  }, [open]);

  if (!actions.length) return null;

  return (
    <div ref={ref} className="relative flex-shrink-0">
      <button
        type="button"
        className={`tg-create ${open ? 'is-open' : ''}`}
        aria-expanded={open}
        aria-haspopup="menu"
        onClick={() => setOpen((v) => !v)}
      >
        <span className="tg-create-icon"><Plus size={14} strokeWidth={2.75} /></span>
        <span className="hidden sm:inline">Create</span>
      </button>
      {open && (
        <div className="tg-create-menu" role="menu" style={{ left: 'auto', right: 0 }}>
          {actions.map((a) => (
            <button
              key={a.label}
              type="button"
              role="menuitem"
              className="tg-nav-item"
              onClick={() => { setOpen(false); a.onSelect(); }}
            >
              <a.icon className="tg-nav-icon mr-3 h-4 w-4" />
              {a.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// Global top bar: menu toggle + logo on the left, a centered jump-to search,
// and a caller-supplied right slot. Spans the full width above the rail so the
// three-zone shell reads as one frame.

// Jump-to search over the nav destinations the user actually has access to.
// 2026-08-30 (user): it must show something the moment you click it, and a
// query must surface EVERY page that matches — not the first six.
//   · empty query  → the full page list, grouped by section ("browse mode")
//   · typed query  → every match across page name AND section name, grouped,
//                    with the matched text highlighted
//   · ⌘K / Ctrl+K focuses it from anywhere; ↑↓ Enter Esc work; list scrolls.
function Highlight({ text, needle }) {
  if (!needle) return text;
  const i = text.toLowerCase().indexOf(needle.toLowerCase());
  if (i < 0) return text;
  return (
    <>
      {text.slice(0, i)}
      <mark className="tg-search-hl">{text.slice(i, i + needle.length)}</mark>
      {text.slice(i + needle.length)}
    </>
  );
}

function JumpSearch({ sections, onNavigate }) {
  const [q, setQ] = React.useState('');
  const [open, setOpen] = React.useState(false);
  const [cursor, setCursor] = React.useState(0);
  const ref = React.useRef(null);
  const inputRef = React.useRef(null);
  const listRef = React.useRef(null);

  // Every reachable page, flat, with its section — the one list both modes read.
  const all = React.useMemo(() => (
    (sections || []).flatMap((s) => (s.items || [])
      // Coming-soon destinations are inert in the sidebar; offering them here
      // would be a back door into a page we aren't showing yet.
      .filter((i) => i.soon !== true)
      .map((i) => ({ ...i, section: s.name })))
  ), [sections]);

  const needle = q.trim().toLowerCase();
  const results = React.useMemo(() => {
    if (!needle) return all;
    const words = needle.split(/\s+/).filter(Boolean);
    const hit = (i) => {
      const hay = `${i.name} ${i.section}`.toLowerCase();
      return words.every((w) => hay.includes(w));
    };
    // Name matches first (what people mean), section-only matches after.
    const byName = all.filter((i) => i.name.toLowerCase().includes(needle));
    const byRest = all.filter((i) => !byName.includes(i) && hit(i));
    return [...byName, ...byRest];
  }, [needle, all]);

  // Grouped for display; the flat `results` order drives keyboard movement.
  const groups = React.useMemo(() => {
    const out = [];
    const idx = new Map();
    results.forEach((item, i) => {
      if (!idx.has(item.section)) { idx.set(item.section, out.length); out.push({ name: item.section, items: [] }); }
      out[idx.get(item.section)].items.push({ item, i });
    });
    return out;
  }, [results]);

  React.useEffect(() => { setCursor(0); }, [q]);

  // Keep the highlighted row in view while arrowing through a long list.
  React.useEffect(() => {
    const el = listRef.current?.querySelector('[aria-selected="true"]');
    if (el && typeof el.scrollIntoView === 'function') el.scrollIntoView({ block: 'nearest' });
  }, [cursor, open]);

  React.useEffect(() => {
    if (!open) return undefined;
    const onDocClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [open]);

  // ⌘K / Ctrl+K from anywhere (not while typing in another field).
  React.useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && String(e.key).toLowerCase() === 'k') {
        e.preventDefault();
        inputRef.current?.focus();
        setOpen(true);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const go = (item) => {
    if (!item) return;
    setQ('');
    setOpen(false);
    inputRef.current?.blur();
    if (item.newTab) { window.open(item.href, '_blank', 'noopener,noreferrer'); return; }
    onNavigate(item.href, item.locked === true);
  };

  const onKeyDown = (e) => {
    if (e.key === 'Escape') { setOpen(false); e.currentTarget.blur(); return; }
    if (!results.length) return;
    if (e.key === 'ArrowDown') { e.preventDefault(); setOpen(true); setCursor((c) => (c + 1) % results.length); }
    if (e.key === 'ArrowUp') { e.preventDefault(); setOpen(true); setCursor((c) => (c - 1 + results.length) % results.length); }
    if (e.key === 'Enter') { e.preventDefault(); go(results[cursor]); }
  };

  const isMac = typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform || '');

  return (
    <div ref={ref} className="tg-search">
      <Search className="tg-search-icon" size={14} aria-hidden="true" />
      <input
        ref={inputRef}
        type="search"
        value={q}
        aria-label="Search pages"
        aria-expanded={open}
        placeholder="Search TeamGrid"
        onChange={(e) => { setQ(e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)}
        onKeyDown={onKeyDown}
      />
      {!open && <span className="tg-search-kbd" aria-hidden="true">{isMac ? '⌘K' : 'Ctrl K'}</span>}
      {open && (
        <div className="tg-search-results" role="listbox" ref={listRef}>
          <div className="tg-search-head">
            <span>{needle ? `${results.length} page${results.length === 1 ? '' : 's'} match “${q.trim()}”` : `Jump to any page · ${all.length} available`}</span>
            <span className="tg-search-hint">↑↓ move · Enter open · Esc close</span>
          </div>
          {results.length === 0 ? (
            <div className="tg-search-empty">No page matches “{q}”. Try a section name — Insights, Team, Settings…</div>
          ) : groups.map((g) => (
            <div key={g.name} className="tg-search-group">
              <div className="tg-search-group-name">{g.name}</div>
              {g.items.map(({ item, i }) => (
                <button
                  key={`${item.href}-${i}`}
                  type="button"
                  role="option"
                  aria-selected={i === cursor}
                  className={`tg-nav-item ${i === cursor ? 'is-active' : ''}`}
                  onMouseEnter={() => setCursor(i)}
                  onClick={() => go(item)}
                >
                  <item.icon className="tg-nav-icon mr-3 h-4 w-4" />
                  <span className="flex-1 truncate text-left"><Highlight text={item.name} needle={needle} /></span>
                  {item.badge ? <span className="tg-search-badge">{item.badge}</span> : null}
                  {item.locked ? <span className="tg-search-badge">Upgrade</span> : null}
                  {i === cursor ? <span className="tg-search-enter">↵</span> : null}
                </button>
              ))}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export function AppTopBar({
  sections, createActions = [], onNavigate, onToggleSidebar, right,
  user, isDark, onToggleTheme, onLogout, showSwitchBack, onSwitchBack,
  trialDaysRemaining, onUpgrade, tasksEnabled = false, idleCount = 0, idleHref,
}) {
  return (
    <header className="tg-topbar">
      <div className="tg-topbar-left">
        <button
          type="button"
          className="tg-icon-btn"
          onClick={onToggleSidebar}
          aria-label="Toggle sidebar"
        >
          <Menu className="h-4 w-4" />
        </button>
        {/* No variant: the logo follows the theme, matching the chrome that
            now flips light/dark with it (LIGHT_SHELL_MOCK.html). */}
        <Logo size={26} />
      </div>

      <JumpSearch sections={sections} onNavigate={onNavigate} />

      <CreateButton actions={createActions} />

      <div className="tg-topbar-right">
        {right}
        {trialDaysRemaining !== null && trialDaysRemaining !== undefined && (
          <button
            type="button"
            onClick={onUpgrade}
            className={`tg-trial-chip${trialDaysRemaining <= 1 ? ' is-urgent' : trialDaysRemaining <= 3 ? ' is-soon' : ''}`}
            title={`Free trial ends in ${trialDaysRemaining} ${trialDaysRemaining === 1 ? 'day' : 'days'} — Upgrade`}
          >
            <span className="tg-trial-chip-icon" aria-hidden>
              <Crown className="h-3 w-3" strokeWidth={2.4} />
            </span>
            <span className="tg-trial-chip-label hidden lg:inline">
              <b>{trialDaysRemaining}</b>
              {trialDaysRemaining === 1 ? ' day left' : ' days left'}
            </span>
          </button>
        )}
        <NotificationBell tasksEnabled={tasksEnabled} idleCount={idleCount} idleHref={idleHref} onNavigate={onNavigate} />
        <AccountMenu
          user={user} isDark={isDark} onToggleTheme={onToggleTheme} onLogout={onLogout}
          onNavigate={onNavigate} showSwitchBack={showSwitchBack} onSwitchBack={onSwitchBack}
        />
      </div>
    </header>
  );
}
